/**
 * TanStack Query hooks for every store-settings domain.
 *
 * One read hook (`use<Domain>Settings`) and one write hook
 * (`useUpdate<Domain>Settings`) per domain, plus the order-lifecycle
 * sub-resource. Provider CRUD and connection-test actions are owned by their
 * reusable UI (`ProviderManager`, `TestConnectionButton`) which compose the
 * generic mutation hooks directly.
 *
 * Every hook delegates HTTP to the typed API clients in `../api`, so screens
 * never touch the transport layer.
 */
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import { getCatalogSettings, updateCatalogSettings } from '../api/catalogSettingsApi';
import { getCheckoutSettings, updateCheckoutSettings } from '../api/checkoutSettingsApi';
import { getContentSettings, updateContentSettings } from '../api/contentSettingsApi';
import { getCustomerSettings, updateCustomerSettings } from '../api/customerSettingsApi';
import { getDeliverySettings, updateDeliverySettings } from '../api/deliverySettingsApi';
import { getEngagementSettings, updateEngagementSettings } from '../api/engagementSettingsApi';
import {
  getNotificationSettings,
  updateNotificationSettings,
} from '../api/notificationSettingsApi';
import {
  getOrderLifecycle,
  getOrderSettings,
  updateOrderLifecycle,
  updateOrderSettings,
} from '../api/orderSettingsApi';
import { getPaymentSettings, updatePaymentSettings } from '../api/paymentSettingsApi';
import { getPricingSettings, updatePricingSettings } from '../api/pricingSettingsApi';
import { getMaxSettings, updateMaxSettings } from '../api/maxSettingsApi';
import { getGeneralSettings, updateGeneralSettings } from '../api/storeSettingsApi';
import { getTelegramSettings, updateTelegramSettings } from '../api/telegramSettingsApi';
import {
  getTelegramChannelSettings,
  updateTelegramChannelSettings,
} from '../api/telegramChannelApi';
import { getTenantSettings, updateTenantSettings } from '../api/tenantSettingsApi';
import {
  getStoreProfileSettings,
  updateStoreProfileSettings,
} from '../api/storeProfileSettingsApi';
import { getAddressSettings, updateAddressSettings } from '../api/addressSettingsApi';
import { getInventorySettings, updateInventorySettings } from '../api/inventorySettingsApi';
import { getMediaSettings, updateMediaSettings } from '../api/mediaSettingsApi';
import { getAiAgentSettings, updateAiAgentSettings } from '../api/aiAgentSettingsApi';
import { getBasketSettings, updateBasketSettings } from '../api/basketSettingsApi';
import { uploadTenantLogo } from '../api/tenantLogoApi';
import type { UploadedAsset, UploadTenantLogoParams } from '../api/tenantLogoApi';
import * as storeServiceApi from '../api/storeServiceApi';
import * as priceListsApi from '../api/priceListsApi';
import {
  bindRetailEvotorStoreLocation,
  createRetailEvotorAccount,
  deleteRetailEvotorAccounts,
  importRetailEvotorDevices,
  importRetailEvotorStores,
  listRetailEvotorAccounts,
  listRetailEvotorDevices,
  listRetailEvotorStores,
  setRetailEvotorStorePriceExport,
  setRetailEvotorStoreStockExport,
  toggleRetailEvotorAccount,
  toggleRetailEvotorStore,
  unbindRetailEvotorStoreLocation,
  updateRetailEvotorAccount,
} from '../api/retailEvotorApi';
import {
  deletePosifloraSettings,
  getPosifloraSettings,
  updatePosifloraCredentials,
  updatePosifloraSettings,
} from '../api/posifloraApi';
import {
  getEvotorIntegrationSettings,
  updateEvotorIntegrationSettings,
} from '../api/evotorIntegrationSettingsApi';
import {
  createSalesChannel,
  deactivateSalesChannel,
  listSalesChannels,
  updateSalesChannel,
  updateTouchpoint,
} from '../api/salesChannelsApi';
import type {
  TenantSalesChannel,
  TenantTouchpoint,
  UpsertTenantSalesChannelRequest,
  UpsertTenantTouchpointRequest,
} from '../api/salesChannelsApi';
import type {
  CreateStoreLocationRequest,
  CreateStoreRegionRequest,
  RetailEvotorAccount,
  RetailEvotorAccountCreateRequest,
  RetailEvotorAccountUpdateRequest,
  RetailEvotorAccountsQuery,
  RetailEvotorDevice,
  RetailEvotorDevicesQuery,
  RetailEvotorEntityDeleteRequest,
  RetailEvotorPage,
  RetailEvotorStore,
  RetailEvotorStoresQuery,
  StoreLocationResponse,
  StoreLocationSearchRequest,
  StoreRegionSearchRequest,
  UpdateStoreLocationRequest,
  UpdateStoreRegionRequest,
} from '../model/settingsTypes';

import { SETTINGS_ROOT_KEY, settingsKeys } from './queryKeys';
import {
  useInvalidatingMutation,
  useSettingsMutation,
  useSettingsQuery,
} from './useSettingsResource';

// ── 1. General ────────────────────────────────────────────────────────────────
export const useGeneralSettings = () => useSettingsQuery(settingsKeys.general, getGeneralSettings);
export const useUpdateGeneralSettings = () =>
  useSettingsMutation(settingsKeys.general, updateGeneralSettings);

// ── 2. Delivery ─────────────────────────────────────────────────────────────
export const useDeliverySettings = () =>
  useSettingsQuery(settingsKeys.delivery, getDeliverySettings);
export const useUpdateDeliverySettings = () =>
  useSettingsMutation(settingsKeys.delivery, updateDeliverySettings);

// ── 3. Payments ───────────────────────────────────────────────────────────────
export const usePaymentSettings = () => useSettingsQuery(settingsKeys.payments, getPaymentSettings);
export const useUpdatePaymentSettings = () =>
  useSettingsMutation(settingsKeys.payments, updatePaymentSettings);

// ── 4. Checkout ───────────────────────────────────────────────────────────────
export const useCheckoutSettings = () =>
  useSettingsQuery(settingsKeys.checkout, getCheckoutSettings);
export const useUpdateCheckoutSettings = () =>
  useSettingsMutation(settingsKeys.checkout, updateCheckoutSettings);

// ── 5. Orders + lifecycle ─────────────────────────────────────────────────────
export const useOrderSettings = () => useSettingsQuery(settingsKeys.orders, getOrderSettings);
export const useUpdateOrderSettings = () =>
  useSettingsMutation(settingsKeys.orders, updateOrderSettings);
export const useOrderLifecycle = () =>
  useSettingsQuery(settingsKeys.ordersLifecycle, getOrderLifecycle);
export const useUpdateOrderLifecycle = () =>
  useSettingsMutation(settingsKeys.ordersLifecycle, updateOrderLifecycle);

// ── 6. Pricing ────────────────────────────────────────────────────────────────
export const usePricingSettings = () => useSettingsQuery(settingsKeys.pricing, getPricingSettings);
export const useUpdatePricingSettings = () =>
  useSettingsMutation(settingsKeys.pricing, updatePricingSettings);

// ── 7. Catalog ────────────────────────────────────────────────────────────────
export const useCatalogSettings = () => useSettingsQuery(settingsKeys.catalog, getCatalogSettings);
export const useUpdateCatalogSettings = () =>
  useSettingsMutation(settingsKeys.catalog, updateCatalogSettings);

// ── 8. Customers ──────────────────────────────────────────────────────────────
export const useCustomerSettings = () =>
  useSettingsQuery(settingsKeys.customers, getCustomerSettings);
export const useUpdateCustomerSettings = () =>
  useSettingsMutation(settingsKeys.customers, updateCustomerSettings);

// ── 9. Notifications ──────────────────────────────────────────────────────────
export const useNotificationSettings = () =>
  useSettingsQuery(settingsKeys.notifications, getNotificationSettings);
export const useUpdateNotificationSettings = () =>
  useSettingsMutation(settingsKeys.notifications, updateNotificationSettings);

// ── 10. Content ───────────────────────────────────────────────────────────────
export const useContentSettings = () => useSettingsQuery(settingsKeys.content, getContentSettings);
export const useUpdateContentSettings = () =>
  useSettingsMutation(settingsKeys.content, updateContentSettings);

// ── 11. Engagement ────────────────────────────────────────────────────────────
export const useEngagementSettings = () =>
  useSettingsQuery(settingsKeys.engagement, getEngagementSettings);
export const useUpdateEngagementSettings = () =>
  useSettingsMutation(settingsKeys.engagement, updateEngagementSettings);

// ── 12. Telegram ──────────────────────────────────────────────────────────────
export const useTelegramSettings = () =>
  useSettingsQuery(settingsKeys.telegram, getTelegramSettings);
export const useUpdateTelegramSettings = () =>
  useSettingsMutation(settingsKeys.telegram, updateTelegramSettings);

// ── 12a. MAX (channel identity + tenant behaviour + transport, merged — /settings/max) ──────
export const useMaxSettings = () => useSettingsQuery(settingsKeys.max, getMaxSettings);
export const useUpdateMaxSettings = () => useSettingsMutation(settingsKeys.max, updateMaxSettings);

// ── 12c. Telegram bot & channel (decoupled from MAX — gap G1) ───────────────
export const useTelegramChannelSettings = () =>
  useSettingsQuery(settingsKeys.telegramChannel, getTelegramChannelSettings);
export const useUpdateTelegramChannelSettings = () =>
  useSettingsMutation(settingsKeys.telegramChannel, updateTelegramChannelSettings);

// ── 13. Tenant ────────────────────────────────────────────────────────────────
export const useTenantSettings = () => useSettingsQuery(settingsKeys.tenant, getTenantSettings);
export const useUpdateTenantSettings = () =>
  useSettingsMutation(settingsKeys.tenant, updateTenantSettings);
export const useUploadTenantLogo = () =>
  useMutation<UploadedAsset, NormalizedApiError, UploadTenantLogoParams>({
    mutationFn: uploadTenantLogo,
  });

// ── 14. Store profile ───────────────────────────────────────────────────────
export const useStoreProfileSettings = () =>
  useSettingsQuery(settingsKeys.store, getStoreProfileSettings);
export const useUpdateStoreProfileSettings = () =>
  useSettingsMutation(settingsKeys.store, updateStoreProfileSettings);

export const useStoreServiceSettings = () =>
  useSettingsQuery(settingsKeys.storeServiceSettings, storeServiceApi.getStoreServiceSettings);
export const useUpdateStoreServiceSettings = () =>
  useSettingsMutation(
    settingsKeys.storeServiceSettings,
    storeServiceApi.updateStoreServiceSettings,
  );

export const useStoreLocations = (params: StoreLocationSearchRequest) =>
  useQuery({
    queryKey: settingsKeys.storeLocations(params),
    queryFn: () => storeServiceApi.searchStoreLocations(params),
    placeholderData: keepPreviousData,
  });

export const useStoreRegions = (params: StoreRegionSearchRequest) =>
  useQuery({
    queryKey: settingsKeys.storeRegions(params),
    queryFn: () => storeServiceApi.searchStoreRegions(params),
    placeholderData: keepPreviousData,
  });

export const useStoreLocationsByRegion = (regionId: string | null) =>
  useQuery({
    queryKey: [...settingsKeys.storeAssignments, 'region', regionId] as const,
    queryFn: () => storeServiceApi.getStoreLocationsByRegion(regionId ?? ''),
    enabled: Boolean(regionId),
  });

export const useStoreRegionsByLocation = (locationId: string | null) =>
  useQuery({
    queryKey: [...settingsKeys.storeAssignments, 'location', locationId] as const,
    queryFn: () => storeServiceApi.getStoreRegionsByLocation(locationId ?? ''),
    enabled: Boolean(locationId),
  });

/**
 * Live address-suggestion lookup backing `AddressAutocompleteField` (`LocationDialog`'s
 * address field). The field debounces free typing itself before passing `query` here, so
 * this hook stays a thin, standard TanStack Query wrapper. `enabled` gates on a 3-character
 * minimum (mirrors the debounced-search UX in `telegram-catalog`'s `AddressFormDialog`, which
 * gates at 4); `placeholderData` keeps the previous suggestion list on screen while a new
 * keystroke's query is in flight instead of flashing empty. `retry: false` — a failed
 * suggestion fetch should fail silently (best-effort convenience feature, never blocks the
 * merchant from typing the address manually).
 */
export function useAddressSuggestions(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: settingsKeys.addressSuggestions(trimmed),
    queryFn: () => storeServiceApi.getAddressSuggestions({ query: trimmed, count: 8 }),
    enabled: trimmed.length >= 3,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: false,
  });
}

function useInvalidateStoreDomain() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: [SETTINGS_ROOT_KEY, 'store'] }),
      qc.invalidateQueries({ queryKey: settingsKeys.storeServiceSettings }),
    ]);
}

/**
 * Typed `StoreLocationResponse` (not `unknown`) — the region-reconciliation flow in
 * `LocationsTab` needs the created/updated location's server-assigned `id` off the
 * resolved mutation value (via `mutateAsync`) to assign/remove region bindings afterward.
 */
export const useCreateStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<StoreLocationResponse, NormalizedApiError, CreateStoreLocationRequest>({
    mutationFn: storeServiceApi.createStoreLocation,
    onSuccess: () => void invalidate(),
  });
};

export const useUpdateStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<
    StoreLocationResponse,
    NormalizedApiError,
    { id: string; body: UpdateStoreLocationRequest }
  >({
    mutationFn: ({ id, body }) => storeServiceApi.updateStoreLocation(id, body),
    onSuccess: () => void invalidate(),
  });
};

export const useDeleteStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: storeServiceApi.deleteStoreLocation,
    onSuccess: () => void invalidate(),
  });
};

export const useToggleStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      active
        ? storeServiceApi.deactivateStoreLocation(id)
        : storeServiceApi.activateStoreLocation(id),
    onSuccess: () => void invalidate(),
  });
};

export const useCreateStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, CreateStoreRegionRequest>({
    mutationFn: storeServiceApi.createStoreRegion,
    onSuccess: () => void invalidate(),
  });
};

export const useUpdateStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; body: UpdateStoreRegionRequest }>({
    mutationFn: ({ id, body }) => storeServiceApi.updateStoreRegion(id, body),
    onSuccess: () => void invalidate(),
  });
};

export const useDeleteStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: storeServiceApi.deleteStoreRegion,
    onSuccess: () => void invalidate(),
  });
};

export const useToggleStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      active ? storeServiceApi.deactivateStoreRegion(id) : storeServiceApi.activateStoreRegion(id),
    onSuccess: () => void invalidate(),
  });
};

export const useAssignStoreLocationToRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { regionId: string; locationId: string }>({
    mutationFn: ({ regionId, locationId }) =>
      storeServiceApi.assignStoreLocationToRegion(regionId, locationId),
    onSuccess: () => void invalidate(),
  });
};

export const useRemoveStoreLocationFromRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<void, NormalizedApiError, { regionId: string; locationId: string }>({
    mutationFn: ({ regionId, locationId }) =>
      storeServiceApi.removeStoreLocationFromRegion(regionId, locationId),
    onSuccess: () => void invalidate(),
  });
};

// ── 15. Address ───────────────────────────────────────────────────────────────
export const useAddressSettings = () => useSettingsQuery(settingsKeys.address, getAddressSettings);
export const useUpdateAddressSettings = () =>
  useSettingsMutation(settingsKeys.address, updateAddressSettings);

// ── 16. Inventory ─────────────────────────────────────────────────────────────
export const useInventorySettings = () =>
  useSettingsQuery(settingsKeys.inventory, getInventorySettings);
export const useUpdateInventorySettings = () =>
  useSettingsMutation(settingsKeys.inventory, updateInventorySettings);

// ── 17. Media ─────────────────────────────────────────────────────────────────
export const useMediaSettings = () => useSettingsQuery(settingsKeys.media, getMediaSettings);
export const useUpdateMediaSettings = () =>
  useSettingsMutation(settingsKeys.media, updateMediaSettings);

// ── 18. AI agents ─────────────────────────────────────────────────────────────
export const useAiAgentSettings = () => useSettingsQuery(settingsKeys.aiAgents, getAiAgentSettings);
export const useUpdateAiAgentSettings = () =>
  useSettingsMutation(settingsKeys.aiAgents, updateAiAgentSettings);

// ── 19. Basket ────────────────────────────────────────────────────────────────
export const useBasketSettings = () => useSettingsQuery(settingsKeys.basket, getBasketSettings);
export const useUpdateBasketSettings = () =>
  useSettingsMutation(settingsKeys.basket, updateBasketSettings);

// ── 21. Retail Evotor cloud-POS ─────────────────────────────────────────────
//
// Accounts/stores/devices are paginated lists (keepPreviousData, mirroring `useStoreLocations`),
// so page changes never re-trigger the page-level `SettingsScaffold` loading state. Most of the
// stateless export/import trigger operations are NOT wrapped here — `SyncTriggerRow` owns a bare
// `useMutation` per row directly against the `retailEvotorApi` functions (no list ever goes stale
// from firing one). Importing stores or devices is the exception: each repopulates a list this
// page shows, so those two invalidate their list's query on success.
export const useRetailEvotorAccounts = (query: RetailEvotorAccountsQuery) =>
  useQuery<RetailEvotorPage<RetailEvotorAccount>, NormalizedApiError>({
    queryKey: settingsKeys.retailEvotorAccounts(query),
    queryFn: () => listRetailEvotorAccounts(query),
    placeholderData: keepPreviousData,
  });

export const useCreateRetailEvotorAccount = () =>
  useInvalidatingMutation<
    RetailEvotorAccount,
    { body: RetailEvotorAccountCreateRequest; idempotencyKey?: string }
  >(settingsKeys.retailEvotorAccountsRoot, ({ body, idempotencyKey }) =>
    createRetailEvotorAccount(body, idempotencyKey),
  );

export const useUpdateRetailEvotorAccount = () =>
  useInvalidatingMutation<
    RetailEvotorAccount,
    { evotorId: string; body: RetailEvotorAccountUpdateRequest }
  >(settingsKeys.retailEvotorAccountsRoot, ({ evotorId, body }) =>
    updateRetailEvotorAccount(evotorId, body),
  );

export const useDeleteRetailEvotorAccounts = () =>
  useInvalidatingMutation<void, RetailEvotorEntityDeleteRequest>(
    settingsKeys.retailEvotorAccountsRoot,
    deleteRetailEvotorAccounts,
  );

/** Credential-gated (AC5) — the row `Switch` in `RetailEvotorAccountsPanel` calls this directly. */
export const useToggleRetailEvotorAccount = () =>
  useInvalidatingMutation<string, { evotorId: string; active: boolean }>(
    settingsKeys.retailEvotorAccountsRoot,
    ({ evotorId, active }) => toggleRetailEvotorAccount(evotorId, active),
  );

export const useRetailEvotorStores = (query: RetailEvotorStoresQuery) =>
  useQuery<RetailEvotorPage<RetailEvotorStore>, NormalizedApiError>({
    queryKey: settingsKeys.retailEvotorStores(query),
    queryFn: () => listRetailEvotorStores(query),
    placeholderData: keepPreviousData,
  });

export const useToggleRetailEvotorStore = () =>
  useInvalidatingMutation<string, { evotorId: string; active: boolean }>(
    settingsKeys.retailEvotorStoresRoot,
    ({ evotorId, active }) => toggleRetailEvotorStore(evotorId, active),
  );

/**
 * Binds/re-binds an Evotor store to an OBZR fulfilment location (plan-integration-010 Phase 6,
 * user-story-integration-024). Invalidates the stores list rather than trusting the mutation's
 * own response shape — the same defensive choice `useToggleRetailEvotorStore` makes above, and
 * doubly warranted here since the response shape is an assumption (see `retailEvotorApi.ts`).
 */
export const useBindRetailEvotorStoreLocation = () =>
  useInvalidatingMutation<RetailEvotorStore, { evotorId: string; locationId: string }>(
    settingsKeys.retailEvotorStoresRoot,
    ({ evotorId, locationId }) => bindRetailEvotorStoreLocation(evotorId, { locationId }),
  );

/** Unbinds an Evotor store from its location (user-story-integration-024). */
export const useUnbindRetailEvotorStoreLocation = () =>
  useInvalidatingMutation<void, { evotorId: string }>(
    settingsKeys.retailEvotorStoresRoot,
    ({ evotorId }) => unbindRetailEvotorStoreLocation(evotorId),
  );

/**
 * Enables/disables the per-store stock-export switch (user-story-integration-025). The panel
 * confirms with the merchant before calling this with `enabled: true` — turning it on overwrites
 * the quantity a cashier currently sees at that store's till.
 */
export const useSetRetailEvotorStoreStockExport = () =>
  useInvalidatingMutation<RetailEvotorStore, { evotorId: string; enabled: boolean }>(
    settingsKeys.retailEvotorStoresRoot,
    ({ evotorId, enabled }) => setRetailEvotorStoreStockExport(evotorId, { enabled }),
  );

/**
 * Enables/disables the per-store PRICE-export switch (adr-integration-017) — the stock-export
 * twin. The panel confirms with the merchant before calling this with `enabled: true` — turning it
 * on overwrites whatever price a cashier currently sees at that store's till.
 */
export const useSetRetailEvotorStorePriceExport = () =>
  useInvalidatingMutation<RetailEvotorStore, { evotorId: string; enabled: boolean }>(
    settingsKeys.retailEvotorStoresRoot,
    ({ evotorId, enabled }) => setRetailEvotorStorePriceExport(evotorId, { enabled }),
  );

// ── Price lists — the Stores tab's "Price list" select (design spec §8.7) ──────────────────────

export const useRetailEvotorPriceLists = () =>
  useQuery<priceListsApi.PriceListOption[], NormalizedApiError>({
    queryKey: settingsKeys.retailEvotorPriceLists,
    queryFn: priceListsApi.listPriceLists,
  });

export interface RetailEvotorPriceListsOverview {
  lists: priceListsApi.PriceListOption[];
  /** storeCode → the list it is currently bound to (across EVERY tenant list, not just one). */
  bindingByStore: Map<string, priceListsApi.PriceListOption>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Composes every tenant price list's own bindings (via `useQueries`, bounded by the tenant's own
 * — typically small — list count, mirroring the `usePriceListsOverview` pattern in
 * `features/catalog`) into a single storeCode→list lookup, so each Stores-tab row can show which
 * list IT is bound to without a per-store reverse-lookup endpoint (none exists).
 */
export const useRetailEvotorPriceListsOverview = (): RetailEvotorPriceListsOverview => {
  const listsQuery = useRetailEvotorPriceLists();
  const lists = listsQuery.data ?? [];

  const bindingsQueries = useQueries({
    queries: lists.map((list) => ({
      queryKey: settingsKeys.retailEvotorPriceListBindings(list.code),
      queryFn: () => priceListsApi.listPriceListBindings(list.code),
    })),
  });

  const bindingByStore = new Map<string, priceListsApi.PriceListOption>();
  lists.forEach((list, index) => {
    const bindings = bindingsQueries[index]?.data ?? [];
    for (const binding of bindings) {
      bindingByStore.set(binding.storeCode, list);
    }
  });

  return {
    lists,
    bindingByStore,
    isLoading: listsQuery.isLoading || bindingsQueries.some((q) => q.isLoading),
    isError: listsQuery.isError || bindingsQueries.some((q) => q.isError),
  };
};

const invalidateRetailEvotorPriceLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: settingsKeys.retailEvotorPriceLists });
};

export const useBindRetailEvotorPriceListStore = () => {
  const queryClient = useQueryClient();
  return useMutation<
    priceListsApi.PriceListBinding,
    NormalizedApiError,
    { code: string; storeCode: string }
  >({
    mutationFn: ({ code, storeCode }) => priceListsApi.bindPriceListStore(code, storeCode),
    onSuccess: () => invalidateRetailEvotorPriceLists(queryClient),
  });
};

export const useUnbindRetailEvotorPriceListStore = () => {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, { code: string; storeCode: string }>({
    mutationFn: ({ code, storeCode }) => priceListsApi.unbindPriceListStore(code, storeCode),
    onSuccess: () => invalidateRetailEvotorPriceLists(queryClient),
  });
};

/**
 * Repopulates the Stores tab/list itself, so — unlike most triggers on this screen — a successful
 * import should refresh the table it lives next to.
 */
export const useImportRetailEvotorStores = () => {
  const queryClient = useQueryClient();
  return useMutation<string, NormalizedApiError, void>({
    mutationFn: () => importRetailEvotorStores(),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: settingsKeys.retailEvotorStoresRoot }),
  });
};

export const useRetailEvotorDevices = (query: RetailEvotorDevicesQuery) =>
  useQuery<RetailEvotorPage<RetailEvotorDevice>, NormalizedApiError>({
    queryKey: settingsKeys.retailEvotorDevices(query),
    queryFn: () => listRetailEvotorDevices(query),
    placeholderData: keepPreviousData,
  });

/** Mirrors {@link useImportRetailEvotorStores} — repopulates the Devices tab/list on success. */
export const useImportRetailEvotorDevices = () => {
  const queryClient = useQueryClient();
  return useMutation<string, NormalizedApiError, void>({
    mutationFn: () => importRetailEvotorDevices(),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: settingsKeys.retailEvotorDevicesRoot }),
  });
};

// ── 22. ERP integrations — Posiflora (MVP: single ERP type) ─────────────────
//
// Not a `VersionedSettings` domain (see `settingsTypes.ts`), so `usePosifloraSettings` /
// `useUpdatePosifloraSettings` are still plain `useSettingsQuery`/`useSettingsMutation` calls —
// those helpers don't require a `version` field, they just read/write whatever shape the query
// key holds. Credentials share the same query key (a successful credentials save also refreshes
// `credentialsConfigured`/`updatedAt` in the cache). Disconnect has no response body to cache, so
// it invalidates instead of `setQueryData`-ing — the next fetch returns the fresh
// `configured: false` state. Connection testing is wired directly through `TestConnectionButton`'s
// `run` prop (`testPosifloraConnection`, exported from `posifloraApi`), matching every other
// provider/engine test-connection action in this feature — no dedicated hook needed.
export const usePosifloraSettings = () =>
  useSettingsQuery(settingsKeys.erpPosiflora, getPosifloraSettings);
export const useUpdatePosifloraSettings = () =>
  useSettingsMutation(settingsKeys.erpPosiflora, updatePosifloraSettings);
export const useUpdatePosifloraCredentials = () =>
  useSettingsMutation(settingsKeys.erpPosiflora, updatePosifloraCredentials);
export const useDisconnectPosiflora = () =>
  useInvalidatingMutation<void, void>(settingsKeys.erpPosiflora, deletePosifloraSettings);

// ── 23. Evotor process-sync integration ──────────────────────────────────────
//
// Not a `VersionedSettings` domain (see `settingsTypes.ts` §23) — no `version` field, so a plain
// `useSettingsQuery`/`useSettingsMutation` pair works unchanged. The PUT response is the BFF's own
// full, fresh resource (recomputed `effectiveEnabled` included), so writing it straight into the
// cache via `useSettingsMutation`'s `setQueryData` gives the same end state as an invalidate+
// refetch would — without predicting anything client-side.
//
// `options.enabled` lets a caller that only needs a *derived* signal (the Retail settings
// Overview tab's launch checklist) skip the fetch entirely when the employee lacks
// `integrations.evotor.view` — that permission is independent of `settings.retail-evotor.*`
// (see `model/permissions.ts`), so the Overview tab cannot assume it is granted.
export const useEvotorIntegrationSettings = (options?: { enabled?: boolean }) =>
  useSettingsQuery(settingsKeys.evotorIntegration, getEvotorIntegrationSettings, options);
export const useUpdateEvotorIntegrationSettings = () =>
  useSettingsMutation(settingsKeys.evotorIntegration, updateEvotorIntegrationSettings);

// ── 24. Sales channels & touchpoints (adr-tenant-001, plan-tenant-001 Phase 5) ──────────────
//
// A registry list with row-level mutations (not a `VersionedSettings` form): every write
// invalidates the one list key — the BFF returns the updated entity, but sibling rows can change
// too (e.g. setting a new default clears the old one), so a full list refetch is the honest sync.
export const useSalesChannelsRegistry = () =>
  useQuery<TenantSalesChannel[], NormalizedApiError>({
    queryKey: settingsKeys.salesChannels,
    queryFn: listSalesChannels,
  });

export const useCreateSalesChannelRegistry = () =>
  useInvalidatingMutation<TenantSalesChannel, UpsertTenantSalesChannelRequest>(
    settingsKeys.salesChannels,
    createSalesChannel,
  );

export const useUpdateSalesChannelRegistry = () =>
  useInvalidatingMutation<
    TenantSalesChannel,
    { code: string; body: UpsertTenantSalesChannelRequest }
  >(settingsKeys.salesChannels, ({ code, body }) => updateSalesChannel(code, body));

export const useDeactivateSalesChannelRegistry = () =>
  useInvalidatingMutation<TenantSalesChannel, string>(
    settingsKeys.salesChannels,
    deactivateSalesChannel,
  );

export const useUpdateTouchpointRegistry = () =>
  useInvalidatingMutation<TenantTouchpoint, { code: string; body: UpsertTenantTouchpointRequest }>(
    settingsKeys.salesChannels,
    ({ code, body }) => updateTouchpoint(code, body),
  );

export { settingsKeys, SETTINGS_ROOT_KEY } from './queryKeys';
export {
  useInvalidatingMutation,
  useSettingsMutation,
  useSettingsQuery,
} from './useSettingsResource';
